import { DateTime, Duration, DurationLike } from 'luxon';
import {
  IEpisode,
  IRecurringEpisodeSchedule,
  MonthRecurrenceType,
  RecurrenceType
} from 'src/app/core/services/extended-program-guide.service';
import advanceMonthRecurrence from './advanceMonthRecurrence';
import findClosestWeekDay from './findClosestWeekDay';
import {
  EpisodeField,
  ScheduleOccurrencesCreationError,
  ScheduleOccurrencesCreationErrorType
} from './formatScheduleOccurrencesCreationError';

export interface IScheduleOccurrence {
  startDate: DateTime;
  endDate: DateTime;
  schedule: IRecurringEpisodeSchedule;
}

export interface IReplicateRecurringScheduleEpisode
  extends Pick<IEpisode, 'start_date' | 'duration'> {}

/**
 * Creates a list of occurrences of an episode, based on the schedule. Errors can be returned in case
 * the given start date is not valid for the given monthly recurrence. For instance, if you receive a
 * start date of 2021-01-31, and a monthly recurrence of every 2 months, on the 30th day of the month,
 * then an error will be returned. The explanation is that the first occurrence will be before the start date,
 * invalidating the schedule.
 *
 * ### `maxSpace` optional parameter
 *
 * The `maxSpace` value is used in case we receive an recurring schedule with no end date (indefinite),
 * and it is used to determine the end date of the occurrences, instead of running forever, since no end date was set.
 *
 * You can also think of this parameter as a default end date for the recurrences with no end date, which are meant to run forever.
 *
 * ### What is the schedule of an episode?
 *
 * The schedule of an episode is the information about when and how many times the episode should run.
 *
 * ### What is the recurrence of an episode?
 *
 * The recurrence of an episode is the information about how many times the episode should run, and how it should occur within the date range (start date and end date).
 *
 * @param episode object with start_date and duration properties
 * @param schedule recurring schedule object
 * @param maxSpace A duration representing the maximum space between the start time and the end of the recurrence, for episodes without an end date. Defaults to 2 weeks.
 * @returns A list of occurrences of the episode, based on the schedule or an error object
 */
export default function getRecurringScheduleOccurrences(
  episode: IReplicateRecurringScheduleEpisode,
  schedule: IRecurringEpisodeSchedule,
  maxSpace = Duration.fromObject({ weeks: 2 })
): IScheduleOccurrence[] | ScheduleOccurrencesCreationError {
  const initialStartDate = DateTime.fromSeconds(episode.start_date);
  let startDate = initialStartDate;
  const endDate =
    schedule.end_date === null
      ? startDate.plus(maxSpace)
      : DateTime.fromSeconds(schedule.end_date);
  const { recurrence } = schedule;
  const occurrences = new Array<IScheduleOccurrence>();
  if (!endDate.isValid) {
    return {
      type: ScheduleOccurrencesCreationErrorType.InvalidEpisodeField,
      field: EpisodeField.EndDate
    };
  }
  if (!startDate.isValid) {
    return {
      type: ScheduleOccurrencesCreationErrorType.InvalidEpisodeField,
      field: EpisodeField.StartDate
    };
  }
  if (recurrence.repeat < 1) {
    return {
      type: ScheduleOccurrencesCreationErrorType.InvalidEpisodeField,
      field: EpisodeField.Every
    };
  }
  const startTime: DurationLike = {
    hour: startDate.hour,
    minute: startDate.minute,
    second: startDate.second,
    millisecond: startDate.millisecond
  };
  let advanceMonth: boolean;
  const occurrenceStartDates = new Array<DateTime>();
  const episodeErrorData = {
    startDate: DateTime.fromSeconds(episode.start_date),
    endDate
  };
  while (startDate.toSeconds() < endDate.toSeconds()) {
    switch (recurrence.type) {
      case RecurrenceType.Day:
        occurrenceStartDates.push(startDate);
        break;
      /**
       * Monthly recurrence are always once every month. Different than weekly recurrence, where a single week can target multiple months
       */
      case RecurrenceType.Month: {
        const monthlyOccurrence = advanceMonthRecurrence(startDate, recurrence);
        /**
         * If it's not the first occurrence, and the next occurrence is before the current start date,
         * it means we already reached all the occurrences of this month, and we should not generate any more occurrences.
         */
        if (monthlyOccurrence.toSeconds() < startDate.toSeconds()) {
          if (!occurrences.length) {
            return {
              type: ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds,
              source: EpisodeField.StartDate,
              affects: EpisodeField.StartDate,
              episode: episodeErrorData,
              date: monthlyOccurrence,
              occurrenceId: occurrences.length
            };
          }
          break;
        }
        occurrenceStartDates.push(monthlyOccurrence);
        break;
      }
      case RecurrenceType.Week:
        if (!recurrence.week_days.length) {
          return {
            type: ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceWeekDayList
          };
        }

        const closestWeekDays = new Array<DateTime>();

        for (const weekDay of recurrence.week_days) {
          closestWeekDays.push(findClosestWeekDay(startDate, weekDay));
        }

        // Maybe impossible scenario, but just in case
        if (!closestWeekDays.length) {
          return {
            type: ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceClosestWeekDays
          };
        }

        for (const date of closestWeekDays) {
          if (date.weekday === startDate.weekday) {
            if (date.toSeconds() > endDate.toSeconds()) {
              /**
               * The occurrence is after the end date of the episode
               */
              return {
                episode: episodeErrorData,
                type: ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds,
                affects: EpisodeField.EndDate,
                date,
                source: EpisodeField.StartDate,
                occurrenceId: occurrences.length + occurrenceStartDates.length
              };
            }
          }
          /**
           * Ignore if the day comes after this week
           */
          if (
            date.endOf('day').toSeconds() > startDate.endOf('week').toSeconds()
          ) {
            continue;
          }
          occurrenceStartDates.push(date);
        }
        break;
    }
    for (const date of occurrenceStartDates.splice(
      0,
      occurrenceStartDates.length
    )) {
      const occurrenceStartDate = date;
      const occurrenceEndDate = occurrenceStartDate.plus({
        minutes: episode.duration
      });
      const occurrenceId = occurrences.length;
      if (occurrenceEndDate.toSeconds() > endDate.toSeconds()) {
        return {
          type: ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds,
          affects: EpisodeField.EndDate,
          episode: episodeErrorData,
          date: occurrenceEndDate,
          source: EpisodeField.EndDate,
          occurrenceId
        };
      }
      if (occurrenceStartDate.toSeconds() > endDate.toSeconds()) {
        return {
          type: ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds,
          episode: episodeErrorData,
          affects: EpisodeField.EndDate,
          date: occurrenceEndDate,
          source: EpisodeField.StartDate,
          occurrenceId
        };
      }
      occurrences.push({
        startDate: occurrenceStartDate,
        endDate: occurrenceEndDate,
        schedule
      });
      startDate = occurrenceStartDate;
    }
    switch (recurrence.type) {
      case RecurrenceType.Day:
        startDate = startDate.plus({
          days: recurrence.repeat
        });
        break;
      case RecurrenceType.Week:
        startDate = startDate.startOf('week').set(startTime).plus({
          weeks: recurrence.repeat
        });
        break;
      case RecurrenceType.Month:
        advanceMonth = false;
        switch (recurrence.recurrence.type) {
          case MonthRecurrenceType.Day:
            if (startDate.day === recurrence.recurrence.day) {
              advanceMonth = true;
            }
            break;
          case MonthRecurrenceType.WeekDay:
            advanceMonth = true;
        }
        if (advanceMonth) {
          startDate = startDate
            .plus({
              months: recurrence.repeat
            })
            .startOf('month')
            .set(startTime);
        }
    }
  }
  return occurrences;
}
